/* Morgan & Reid — "Complete your set" PDP recommendation section (Phase 2, v2 hierarchy, polish p8.1).
 * STANDALONE renderer. Same resolver output as the cart engine (one resolver, two renderers):
 * reads /api/cross-sell, picks THIS product's queue, renders FEATURED + SECONDARY hierarchy.
 *
 * v2 UX (featured + secondary — one decision presented properly, the rest quiet):
 *  - FEATURED = the engine's #1 renderable-and-not-owned item: large image, title, rating
 *    (real Judge.me only), reason line, price, variant select, full-width CTA.
 *  - SECONDARY = #2 and #3 under "More essentials": compact rows (small thumb, title,
 *    one-line reason, price, compact Add). One result -> featured alone, no subheading.
 *  - OWNERSHIP-AWARE RE-RENDER (v2 flow change, per 2026-07-17 brief — supersedes the v1
 *    frozen-identity/"In your cart ✓" behaviour): selection skips items already in the cart,
 *    and every cart signal re-selects, so the featured slot is always the highest-priority
 *    item NOT in the cart. The resolver is cart-agnostic; reading /cart.js here is the same
 *    ownership filtering the cart widget does — no resolver second-guessing.
 *  - NO dismiss on the PDP (cart-engine concept; was never here — preserved absent).
 *
 * STYLES: injected <style> from this file (mr-pdpx-*). Deliberate: the PDP's old styles live
 * in the SHARED mr-cross-sell.css (cart drawer card), which is scope-guarded — injecting
 * confines this redesign's diff to this one file. The old .mr-xsell-card--pdp rules there
 * are simply unused now.
 *
 * Shared standards (unchanged): fail-closed config (master toggle gates both surfaces),
 * vendor lock + precise-or-nothing from the resolver, short display titles, source-driven
 * reason lines, source-aware CTAs ("matching" ONLY on colour links), real ratings only,
 * available-only variants with image+price swap on change (both slots), sentence case,
 * no emoji, no urgency, no green (✓ Added = charcoal/cream), Redo-safe add path, NO
 * fetch/XHR monkeypatching, renders ONLY inside #mr-xsell-pdp, zero form/submit listeners
 * (bundle-builder clearance).
 */
(function () {
  "use strict";

  var API_BASE = "https://commerce-command-79465365634.australia-southeast1.run.app";
  var SHOP = (window.Shopify && window.Shopify.shop) || "manchester-factory-australia.myshopify.com";
  var ROUTE_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var SLOT_ID = "mr-xsell-pdp";
  var XSELL_PROP = "_mr_cross_sell";
  // 4 survivors selected so the pillowcase PAIR can consume the featured tier while the
  // "You may also need" rows still show the next two queue items (4 products max on screen).
  // Non-pair renders cap at 3 (featured + 2 rows) as before.
  var MAX_ITEMS = 4;

  var slot = document.getElementById(SLOT_ID);
  if (!slot) return;
  var PRODUCT_ID = String(slot.getAttribute("data-product-id") || "").replace(/\D/g, "");
  if (!PRODUCT_ID) return;

  var configCache = null;    // fetched once per page load
  var adding = false;        // one add in flight
  var holdRender = false;    // suppress re-render while the ✓ Added beat plays
  var lastFeaturedPid = null;
  var lastKnownCart = null;  // most recent cart from ANY source — the degrade target when a
                             // /cart.js read is killed by the third-party fetch wrappers
                             // (never fall back to empty once ownership is known)
  var preferredSize = null;  // the PDP's currently selected comforter size (e.g. "Queen") —
                             // drives sheet-variant pre-selection in warmth-sourced selectors
  var sizeSyncSelects = [];  // warmth-source selects in the current render, for re-sync

  function j(url, opts) { return fetch(url, opts).then(function (r) { return r.ok ? r.json() : Promise.reject(r); }); }
  function num(v) { return String(v == null ? "" : v).replace(/\D/g, ""); }
  function getCart() { return j(ROUTE_ROOT + "cart.js"); }

  function getConfig() {
    if (!configCache) {
      configCache = j(API_BASE + "/api/cross-sell?shop=" + encodeURIComponent(SHOP))
        .then(function (cfg) { return (cfg && cfg.enabled === true && cfg.queues && cfg.queues.length) ? cfg : { enabled: false }; })
        .catch(function () { return { enabled: false }; });
    }
    return configCache;
  }

  // ── Display copy helpers — kept in lockstep with mr-cross-sell.js. ──
  function collapse(s) { return s.replace(/\s+/g, " ").trim(); }

  function shortTriggerName(title) {
    var t = String(title || "");
    t = t.replace("Morgan and Reid ", "").replace(" Comforter Set", "").replace(" Coverlet Set", "").replace(" Coverlet", "").replace(" Fleece", "");
    return collapse(t);
  }

  // Anchor noun from the queue's triggerType (additive API field). Missing/unrecognised ->
  // "comforter": old API shapes and comforter anchors render today's copy verbatim.
  function anchorNoun(triggerType) {
    return /coverlet/i.test(String(triggerType || "")) ? "coverlet" : "comforter";
  }

  function rangeWord(title) {
    var t = String(title || "");
    if (/rabbit/i.test(t)) return "Rabbit Fur";
    if (/checkered/i.test(t)) return "Checkered";
    if (/velvet/i.test(t)) return "Velvet";
    if (/snuggle/i.test(t)) return "Snuggle";
    return null;
  }

  function shortDisplayTitle(title) {
    var t = String(title || "").replace("Morgan and Reid ", "");
    t = t.replace(" Snuggle Fleece ", " ").replace(" Velvet Crushed ", " Velvet ").replace(" Velvet Stripe ", " Velvet ");
    var SUFFIX = [
      ["European Pillowcase Pair", "European Pillowcases"],
      ["Pillowcase Pair", "Pillowcases"],
      ["Sheet Set", "Sheets"],
      ["Throw Rug", "Throw"],
    ];
    for (var i = 0; i < SUFFIX.length; i++) {
      var at = t.indexOf(SUFFIX[i][0]);
      if (at !== -1) { t = t.slice(0, at) + SUFFIX[i][1] + t.slice(at + SUFFIX[i][0].length); break; }
    }
    return collapse(t);
  }

  // Category noun for CTAs — strict title match; null when underivable (fall back to plain CTA).
  function categoryNoun(title) {
    var t = String(title || "");
    if (/european pillowcase|pillowcase/i.test(t)) return "pillowcases";
    if (/sheet set/i.test(t)) return "sheets";
    if (/blanket/i.test(t)) return "blanket";
    if (/throw/i.test(t)) return "throw";
    if (/storage bag/i.test(t)) return "storage bag";
    if (/cushion/i.test(t)) return "cushion";
    return null;
  }

  function reasonFor(item, triggerTitle, triggerWarmth, triggerType) {
    var src = item.source;
    var noun = anchorNoun(triggerType);
    var Noun = noun === "coverlet" ? "Coverlet" : "Comforter";
    var trig = triggerTitle ? shortTriggerName(triggerTitle) : null;
    if (src === "colourLink") return trig ? "Perfectly matched to your " + trig + " " + Noun + "." : "Perfectly matched to your " + noun + ".";
    if (src === "warmth") {
      var w = String(triggerWarmth || "");
      var season = (w === "Winter" || w === "Extra Warm") ? "winter" : w === "Summer" ? "summer" : w === "All Seasons" ? "all-seasons" : null;
      return season ? "Designed to pair with your " + season + " " + noun + "." : "Designed to pair with your " + noun + ".";
    }
    if (src === "rangeDefault") {
      // Storage bag gets its own outcome line in EVERY slot (featured and rows alike).
      if (/storage bag/i.test(String(item.title || ""))) {
        return noun === "coverlet" ? "Store your bedding neatly between seasons." : "Keep your comforter clean between seasons.";
      }
      var rw = triggerTitle ? rangeWord(triggerTitle) : null;
      return rw ? "Completes your " + rw + " set." : "Completes your set.";
    }
    if (src === "override") return trig ? "Chosen for your " + trig + " " + Noun + "." : "Chosen for you.";
    return "";
  }

  // Source-aware CTA — "matching" ONLY on colour links; noun from category, else plain fallback.
  function ctaFor(item) {
    var noun = categoryNoun(item.title);
    if (item.source === "colourLink" && noun) return "Add matching " + noun;
    if (noun) return "Add " + noun;
    return "Add to cart";
  }

  // Heading varies only on data the resolver already returns (featured item's title/source).
  function headingFor(item, triggerType) {
    if (/storage bag/i.test(String(item.title || ""))) return "Protect your " + anchorNoun(triggerType);
    if (item.source === "warmth") return "Complete your bedroom";
    return "Complete your set";
  }

  // Featured-slot reason line — slightly more aspirational than the row version, still strictly
  // source-driven ("matching" only on colour links; no urgency, no generic filler). Rows keep
  // reasonFor() unchanged.
  function featuredReasonFor(item, triggerTitle, triggerWarmth, triggerType) {
    if (/storage bag/i.test(String(item.title || ""))) {
      return anchorNoun(triggerType) === "coverlet" ? "Store your bedding neatly between seasons." : "Keep your comforter clean between seasons.";
    }
    if (item.source === "colourLink") {
      var noun = categoryNoun(item.title);
      if (noun) return "Complete the look with perfectly matched " + noun + ".";
    }
    return reasonFor(item, triggerTitle, triggerWarmth, triggerType);
  }

  function money(p) {
    var n = Number(p);
    if (isNaN(n)) return String(p);
    if (String(p).indexOf(".") === -1 && n === Math.floor(n) && n >= 100) n = n / 100;
    return "$" + n.toFixed(2);
  }

  // ── Redo-safe re-render after an add (identical to the cart widget's path; never opens the
  //    drawer). Returns the FULL CART from cart/update.js — the caller renders from it directly,
  //    because a separate /cart.js read is intermittently killed by third-party fetch wrappers
  //    (cart-insights/eraya/wpm) and a failed read would hide ownership from the promotion pass. ──
  function refreshDrawer() {
    var sections = [];
    try { document.documentElement.dispatchEvent(new CustomEvent("cart:bundled-sections", { bubbles: true, detail: { sections: sections } })); } catch (e) {}
    var pubsub = window.theme && window.theme.pubsub;
    return j(ROUTE_ROOT + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ sections: sections, sections_url: window.location.pathname }),
    }).then(function (cart) {
      if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate && cart && cart.sections) {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: "pdp-cross-sell", cart: cart });
      } else {
        try { document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } })); } catch (e) {}
      }
      return cart;
    }).catch(function () { return null; });
  }

  function addToCart(btn, item, queueId, variantId) {
    if (adding || btn.disabled) return;
    adding = true;
    holdRender = true;
    btn.disabled = true;
    btn.textContent = "Adding…";
    var properties = {};
    properties[XSELL_PROP] = String(queueId);
    j(ROUTE_ROOT + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: properties }] }),
    }).then(function () {
      // ✓ Added beat (charcoal/cream), brief hold, then the queue re-render promotes the next
      // highest-priority not-in-cart item into the featured slot.
      btn.textContent = "✓ Added";
      btn.classList.add("mr-pdpx-added");
      // v2.1 promotion transition: ✓ Added holds ~500ms, outgoing content fades/slides out,
      // promoted content slides up (enter animation), heights reserved during the swap.
      return new Promise(function (res) { setTimeout(res, 500); }).then(refreshDrawer).then(function (cart) {
        holdRender = false;
        // Promote directly from the cart/update.js response (authoritative, wrapper-proof);
        // fall back to a normal scheduled pass only if that call itself failed.
        if (cart && cart.items) {
          lastKnownCart = cart;
          return getConfig().then(function (cfg) { if (cfg.enabled === true) transitionRender(cfg, cart); });
        }
        schedule();
      });
    }).catch(function (e) {
      if (window.console) console.log("[pdp-cross-sell] add error:", e);
      btn.disabled = false;
      btn.textContent = btn.getAttribute("data-cta") || "Add to cart";
      btn.classList.remove("mr-pdpx-added");
      holdRender = false;
    }).then(function () { adding = false; });
  }

  // ── Selection: this product's queue -> renderable (available variants) and NOT owned. ──
  function selectEntries(cfg, cart) {
    var queue = null;
    for (var q = 0; q < cfg.queues.length; q++) {
      var trig = cfg.queues[q].triggerProductIds || [];
      for (var t = 0; t < trig.length; t++) if (num(trig[t]) === PRODUCT_ID) { queue = cfg.queues[q]; break; }
      if (queue) break;
    }
    if (!queue) return [];
    var owned = {};
    for (var c = 0; c < cart.items.length; c++) owned[num(cart.items[c].product_id)] = true;
    var out = [];
    var items = queue.items || [];
    for (var i = 0; i < items.length && out.length < MAX_ITEMS; i++) {
      if (owned[num(items[i].productId)]) continue; // featured is always highest-priority NOT in cart
      var avail = (items[i].variants || []).filter(function (v) { return v.available === true; });
      if (!avail.length) continue;
      out.push({
        item: items[i], avail: avail, queueId: queue.id,
        triggerTitle: queue.triggerTitle || null, triggerWarmth: queue.triggerWarmth || null, triggerType: queue.triggerType || null,
      });
    }
    return out;
  }

  // ── DOM builders ──
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function buildRating(item) {
    var r = item.rating;
    if (!(r && Number(r.value) > 0 && Number(r.count) > 0)) return null;
    return el("div", "mr-pdpx-rating", "★ " + Number(r.value).toFixed(1) + " (" + Number(r.count) + ")");
  }

  // ── Sheet size pre-selection. MATCHING RULE: exact, case-insensitive equality between the
  //    comforter's selected size (variant title, segment before any "/") and the sheet variant's
  //    size segment (same rule); FIRST available match is pre-selected. No match / nothing
  //    available -> default behaviour. A selector the customer has touched (trusted change
  //    event) is never overridden. ──
  function sizeOf(title) { return String(title || "").split("/")[0].trim().toLowerCase(); }
  // A manual sheet-variant choice holds for the whole session (survives comforter size flips
  // and re-renders). Storage failures degrade to per-render behaviour, never an error.
  var SHEET_CHOICE_KEY = "mr_pdpx_sheet_choice";
  function getSheetChoice(productId) {
    try { var m = JSON.parse(window.sessionStorage.getItem(SHEET_CHOICE_KEY) || "{}"); return m[num(productId)] || null; } catch (e) { return null; }
  }
  function storeSheetChoice(productId, variantId) {
    try {
      var m = JSON.parse(window.sessionStorage.getItem(SHEET_CHOICE_KEY) || "{}");
      m[num(productId)] = String(variantId);
      window.sessionStorage.setItem(SHEET_CHOICE_KEY, JSON.stringify(m));
    } catch (e) {}
  }
  function readComforterSize() {
    try {
      var s = document.querySelector('variant-picker script[type="application/json"][data-selected-variant]');
      var v = s ? JSON.parse(s.textContent) : null;
      return v && v.title ? sizeOf(v.title) : null;
    } catch (e) { return null; }
  }
  function applyPreferredSize(rec) {
    if (rec.select.dataset.user === "1") return;
    if (preferredSize) {
      for (var i = 0; i < rec.variants.length; i++) {
        if (rec.variants[i].available === true && sizeOf(rec.variants[i].title) === preferredSize) {
          if (rec.select.value !== String(rec.variants[i].id)) {
            rec.select.value = String(rec.variants[i].id);
            rec.select.dispatchEvent(new Event("change")); // updates price + image; not "trusted"
          }
          if (rec.helper) rec.helper.hidden = false; // the claim is now true
          return;
        }
      }
    }
    if (rec.helper) rec.helper.hidden = true; // no match -> never falsely claim one
  }
  function syncSizeSelects() { sizeSyncSelects.forEach(applyPreferredSize); }

  function buildSelect(entry, priceEl, imgEl) {
    if (entry.avail.length < 2) return null;
    var select = el("select", "mr-pdpx-select");
    select.setAttribute("aria-label", "Choose a variant");
    for (var i = 0; i < entry.avail.length; i++) {
      var opt = document.createElement("option");
      opt.value = String(entry.avail[i].id);
      opt.textContent = entry.avail[i].title;
      select.appendChild(opt);
    }
    var variants = entry.avail, fallback = entry.item.image || null;
    select.addEventListener("change", function () {
      for (var v = 0; v < variants.length; v++) {
        if (String(variants[v].id) === select.value) {
          if (priceEl) priceEl.textContent = money(variants[v].price);
          if (imgEl) {
            var want = variants[v].image || fallback;
            if (want && imgEl.src !== want) imgEl.src = want;
          }
          break;
        }
      }
    });
    if (entry.item.source === "warmth") {
      // Helper line rides with the selector (caller appends it right after the select).
      var helper = el("div", "mr-pdpx-size-helper", "Pre-selected to match your comforter.");
      helper.hidden = true;
      select._mrHelper = helper;
      var rec = { select: select, variants: entry.avail, helper: helper };
      select.addEventListener("change", function (e) {
        if (!e.isTrusted) return;
        select.dataset.user = "1";                       // their choice holds...
        storeSheetChoice(entry.item.productId, select.value); // ...for the whole session
        helper.hidden = true;                            // the "matched" claim no longer applies
      });
      sizeSyncSelects.push(rec);
      var stored = getSheetChoice(entry.item.productId);
      if (stored && entry.avail.some(function (v) { return String(v.id) === stored; })) {
        select.dataset.user = "1";
        if (select.value !== stored) { select.value = stored; select.dispatchEvent(new Event("change")); }
      } else {
        applyPreferredSize(rec);
      }
    } else {
      select.addEventListener("change", function (e) { if (e.isTrusted) select.dataset.user = "1"; });
    }
    return select;
  }

  function wireAdd(btn, entry, select, ctaOverride) {
    var cta = ctaOverride || ctaFor(entry.item);
    btn.textContent = cta;
    btn.setAttribute("data-cta", cta);
    btn.addEventListener("click", function () {
      addToCart(btn, entry.item, entry.queueId, select ? select.value : String(entry.avail[0].id));
    });
  }

  // ── Pillowcase pair (featured tier). Detection is deliberately guarded: BOTH items must be
  //    colour-link source AND pillowcase-titled, with exactly one European — title keying alone
  //    is not enough (proper group metadata is a phase 1.5 design item). A lone pillowcase
  //    (no link, or the other OOS/owned) renders as the normal single featured card. ──
  function isPillowcasePair(a, b) {
    if (!a || !b) return false;
    if (a.item.source !== "colourLink" || b.item.source !== "colourLink") return false;
    var ta = String(a.item.title || ""), tb = String(b.item.title || "");
    if (!/pillowcase/i.test(ta) || !/pillowcase/i.test(tb)) return false;
    return /european/i.test(ta) !== /european/i.test(tb);
  }

  // Differentiated pair CTAs (v2.1). "Matching" lives in the shared reason line, not the CTAs.
  function pairCta(item) {
    return /european/i.test(String(item.title || "")) ? "Add european pillowcases" : "Add pillowcase pair";
  }

  // ── Standardised pair-card names: line 1 category, line 2 colour, parsed from the title.
  //    PARSE RULE: strip "Morgan and Reid " prefix; category = the "European Pillowcase Pair" /
  //    "Pillowcase Pair" suffix; colour = the remainder with known range tokens removed
  //    (Snuggle Fleece / Rabbit Fur / Checkered / Velvet Crushed / Velvet Stripe / Velvet /
  //    Heavy Weight). Valid only if the residue is non-empty, ≤26 chars, and contains no
  //    leftover product words. PARSE-OR-FALLBACK is ALL-OR-NOTHING: if either card fails,
  //    BOTH render the full product title — never one standardised and one raw. ──
  function pairName(item) {
    var t = collapse(String(item.title || "").replace("Morgan and Reid ", ""));
    var category = null;
    if (/European Pillowcase Pair$/i.test(t)) { category = "European pillowcases"; t = t.replace(/\s*European Pillowcase Pair$/i, ""); }
    else if (/Pillowcase Pair$/i.test(t)) { category = "Pillowcase pair"; t = t.replace(/\s*Pillowcase Pair$/i, ""); }
    if (!category) return null;
    var colour = collapse(t
      .replace(/Snuggle Fleece/gi, "").replace(/Rabbit Fur/gi, "").replace(/Checkered/gi, "")
      .replace(/Velvet Crushed/gi, "").replace(/Velvet Stripe/gi, "").replace(/Velvet/gi, "")
      .replace(/Heavy Weight/gi, ""));
    if (!colour || colour.length > 26 || /fleece|pillowcase|pair|comforter/i.test(colour)) return null;
    return { category: category, colour: colour };
  }

  function buildPairCard(entry, name) {
    var card = el("div", "mr-pdpx-pair-card");
    var img = null;
    if (entry.item.image) {
      var media = el("div", "mr-pdpx-pair-media");
      img = document.createElement("img");
      img.className = "mr-pdpx-pair-image";
      img.src = entry.item.image;
      img.alt = entry.item.title || "";
      img.loading = "lazy";
      media.appendChild(img);
      card.appendChild(media);
    }
    var info = el("div", "mr-pdpx-pair-info");
    card.appendChild(info);
    // v2.1 card order: category / colour / merch line / badge / price / CTA. No stars here
    // (the single featured card keeps its rating; pair cards deliberately don't).
    if (name) {
      info.appendChild(el("div", "mr-pdpx-pair-title", name.category));
      info.appendChild(el("div", "mr-pdpx-pair-colour", name.colour));
    } else {
      info.appendChild(el("div", "mr-pdpx-pair-title", entry.item.title));
    }
    // Per-card merch line CUT (Dimi-delegated judgement 17/07: with the shared intro AND the
    // badge, three "match" claims read repetitive — the per-card line was nominated to go).
    info.appendChild(el("div", "mr-pdpx-pair-badge", "✓ Perfect match"));
    var price = el("div", "mr-pdpx-pair-price", money(entry.avail[0].price));
    info.appendChild(price);
    var select = buildSelect(entry, price, img);
    if (select) { info.appendChild(select); if (select._mrHelper) info.appendChild(select._mrHelper); }
    var btn = el("button", "mr-pdpx-cta mr-pdpx-pair-btn");
    btn.type = "button";
    wireAdd(btn, entry, select, pairCta(entry.item));
    info.appendChild(btn);
    return card;
  }

  function buildPair(a, b) {
    var wrap = el("div", "mr-pdpx-pair-wrap");
    wrap.appendChild(el("div", "mr-pdpx-pair-reason", "Finish your set with matching pillowcases."));
    var pair = el("div", "mr-pdpx-pair");
    // All-or-nothing standardised names: both parse cleanly or both fall back to full titles.
    var na = pairName(a.item), nb = pairName(b.item);
    var ok = !!(na && nb);
    pair.appendChild(buildPairCard(a, ok ? na : null));
    pair.appendChild(buildPairCard(b, ok ? nb : null));
    wrap.appendChild(pair);
    return wrap;
  }

  function buildFeatured(entry) {
    var card = el("div", "mr-pdpx-featured");
    // Two-column desktop / stacked mobile. The image sits in a fixed-aspect CONTAINER
    // (dimensions reserved before load — no layout shift; product framed, never a texture strip).
    var cols = el("div", "mr-pdpx-cols");
    card.appendChild(cols);
    var img = null;
    if (entry.item.image) {
      var media = el("div", "mr-pdpx-media");
      img = document.createElement("img");
      img.className = "mr-pdpx-featured-image";
      img.src = entry.item.image;
      img.alt = entry.item.title || "";
      img.loading = "lazy";
      media.appendChild(img);
      cols.appendChild(media);
    }
    var body = el("div", "mr-pdpx-featured-body");
    cols.appendChild(body);
    body.appendChild(el("div", "mr-pdpx-featured-title", shortDisplayTitle(entry.item.title)));
    var rating = buildRating(entry.item);
    if (rating) body.appendChild(rating);
    var reason = featuredReasonFor(entry.item, entry.triggerTitle, entry.triggerWarmth, entry.triggerType);
    if (reason) body.appendChild(el("div", "mr-pdpx-reason", reason));
    var price = el("div", "mr-pdpx-featured-price", money(entry.avail[0].price));
    body.appendChild(price);
    var select = buildSelect(entry, price, img);
    if (select) { body.appendChild(select); if (select._mrHelper) body.appendChild(select._mrHelper); }
    var btn = el("button", "mr-pdpx-cta");
    btn.type = "button";
    wireAdd(btn, entry, select);
    // Full-card-width CTA below the columns (chosen over in-column after comparing both:
    // the wide bar anchors the card and keeps one strong tap target on every viewport).
    card.appendChild(btn);
    return card;
  }

  function buildRow(entry) {
    var row = el("div", "mr-pdpx-row");
    var img = null;
    if (entry.item.image) {
      img = document.createElement("img");
      img.className = "mr-pdpx-row-thumb";
      img.src = entry.item.image;
      img.alt = entry.item.title || "";
      img.loading = "lazy";
      row.appendChild(img);
    }
    var info = el("div", "mr-pdpx-row-info");
    row.appendChild(info);
    info.appendChild(el("div", "mr-pdpx-row-title", shortDisplayTitle(entry.item.title)));
    var reason = reasonFor(entry.item, entry.triggerTitle, entry.triggerWarmth, entry.triggerType);
    if (reason) info.appendChild(el("div", "mr-pdpx-row-reason", reason));
    var price = el("div", "mr-pdpx-row-price", money(entry.avail[0].price));
    info.appendChild(price);
    var select = buildSelect(entry, price, img);
    if (select) { info.appendChild(select); if (select._mrHelper) info.appendChild(select._mrHelper); }
    var btn = el("button", "mr-pdpx-row-add");
    btn.type = "button";
    wireAdd(btn, entry, select);
    row.appendChild(btn);
    return row;
  }

  function render(cfg, cart) {
    var mount = document.getElementById(SLOT_ID);
    if (!mount) return;
    if (!cfg || cfg.enabled !== true) return; // fail closed: render nothing, remove nothing
    sizeSyncSelects = []; // rebuilt with the DOM each render
    var entries = selectEntries(cfg, cart);
    if (!entries.length) { mount.innerHTML = ""; lastFeaturedPid = null; return; } // precise-or-nothing

    var section = el("div", "mr-pdpx-section");
    var featuredPid = num(entries[0].item.productId);
    if (featuredPid !== lastFeaturedPid) section.className += " mr-pdpx-enter";
    lastFeaturedPid = featuredPid;

    section.appendChild(el("div", "mr-pdpx-heading", headingFor(entries[0].item, entries[0].triggerType)));

    // Pair state: both colour-linked pillowcases survive -> grouped container in the featured
    // tier; rows show the NEXT queue survivors. Otherwise: single featured + up to 2 rows.
    var rowEntries;
    if (isPillowcasePair(entries[0], entries[1])) {
      section.appendChild(buildPair(entries[0], entries[1]));
      rowEntries = entries.slice(2);
    } else {
      section.appendChild(buildFeatured(entries[0]));
      rowEntries = entries.slice(1, 3);
    }

    if (rowEntries.length) {
      // Rows heading with dedupe: never the same heading twice in one render.
      var rowsHeading = headingFor(entries[0].item, entries[0].triggerType) === "Complete your bedroom" ? "You may also need" : "Complete your bedroom";
      section.appendChild(el("div", "mr-pdpx-subheading", rowsHeading));
      var rows = el("div", "mr-pdpx-rows");
      for (var i = 0; i < rowEntries.length; i++) rows.appendChild(buildRow(rowEntries[i]));
      section.appendChild(rows);
    }

    mount.innerHTML = "";
    mount.appendChild(section);
  }

  // Animated swap for the post-add promotion: leave (fade/slide out) -> re-render (enter
  // animation slides the promoted content up) -> release the reserved height. Reduced motion
  // or no current section -> instant swap.
  function transitionRender(cfg, cart) {
    var reduce = false;
    try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
    var mount = document.getElementById(SLOT_ID);
    var sec = mount ? mount.querySelector(".mr-pdpx-section") : null;
    if (reduce || !sec) { render(cfg, cart); return; }
    mount.style.minHeight = mount.offsetHeight + "px"; // reserve height — no jank below
    sec.classList.add("mr-pdpx-leave");
    setTimeout(function () {
      render(cfg, cart);
      setTimeout(function () { mount.style.minHeight = ""; }, 320);
    }, 200);
  }

  // ── One pass per cart signal (queue re-render): cached config + fresh cart. ──
  var timer = null, chain = Promise.resolve();
  function passOnce() {
    if (holdRender) return Promise.resolve();
    return getConfig().then(function (cfg) {
      if (cfg.enabled !== true) return;
      // Cart read can be broken transiently by third-party fetch wrappers (cart-insights/eraya/wpm
      // chain throws "Failed to fetch" even same-origin). Degrade to the LAST KNOWN cart (empty
      // only when we've never seen one) so a blocked read can't erase known ownership state.
      return getCart()
        .then(function (cart) { lastKnownCart = cart; return cart; })
        .catch(function () { return lastKnownCart || { items: [] }; })
        .then(function (cart) { render(cfg, cart); });
    }).catch(function (e) { if (window.console) console.log("[pdp-cross-sell] pass error:", e); });
  }
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(function () { chain = chain.then(passOnce).catch(function () {}); }, 250);
  }

  // When a theme signal carries the cart payload, render from it directly (wrapper-proof);
  // otherwise fall back to a scheduled /cart.js pass.
  function renderWithCart(cart) {
    lastKnownCart = cart;
    if (holdRender) return;
    getConfig().then(function (cfg) { if (cfg.enabled === true) render(cfg, cart); });
  }
  function wire() {
    // Comforter size changes re-sync warmth-source selectors (capture phase — the theme's
    // variant:change doesn't bubble reliably; same wiring the bundle builder uses).
    document.addEventListener("variant:change", function (e) {
      var v = e.detail && e.detail.variant;
      if (v && v.title) { preferredSize = sizeOf(v.title); syncSizeSelects(); }
    }, true);
    document.addEventListener("cart:updated", function (e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && cart.items) renderWithCart(cart); else schedule();
    });
    document.addEventListener("cart:refresh", schedule);
    try {
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS && theme.pubsub.PUB_SUB_EVENTS.cartUpdate) {
        theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
          var cart = payload && payload.cart;
          if (cart && cart.items) renderWithCart(cart); else schedule();
        });
      }
    } catch (e) {}
  }

  // ── Injected styles (scoped mr-pdpx-*). Static flow only: no position/z-index that could
  //    interact with the sticky ATC pill; animation is opacity/transform on the section. ──
  function injectStyles() {
    if (document.getElementById("mr-pdpx-styles")) return;
    var css = "" +
      "#mr-xsell-pdp{display:block}#mr-xsell-pdp:empty{display:none}" +
      ".mr-pdpx-section{font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;color:#2C2C2A;margin:4px 0}" +
      ".mr-pdpx-section *,.mr-pdpx-section *::before,.mr-pdpx-section *::after{box-sizing:border-box}" +
      ".mr-pdpx-section.mr-pdpx-enter{animation:mr-pdpx-in .22s ease both}" +
      "@keyframes mr-pdpx-in{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}" +
      ".mr-pdpx-section{transition:opacity .2s ease,transform .2s ease}" +
      ".mr-pdpx-section.mr-pdpx-leave{opacity:0;transform:translateY(-8px)}" +
      "@media (prefers-reduced-motion:reduce){.mr-pdpx-section.mr-pdpx-enter{animation:none}.mr-pdpx-section{transition:none}}" +
      ".mr-pdpx-heading{font-size:15px;font-weight:600;letter-spacing:.01em;margin-bottom:8px}" +
      ".mr-pdpx-featured{background:#F1F0EC;border:1px solid rgba(44,44,42,.08);border-radius:10px;padding:16px}" +
      ".mr-pdpx-cols{display:flex;gap:14px;align-items:flex-start}" +
      ".mr-pdpx-media{flex:0 0 38%;max-width:220px;aspect-ratio:1/1;border-radius:8px;overflow:hidden;background:#fff}" +
      ".mr-pdpx-featured-image{display:block;width:100%;height:100%;object-fit:cover;margin:0!important}" +
      "@media (max-width:519px){.mr-pdpx-cols{display:block}.mr-pdpx-media{width:70%;max-width:none;aspect-ratio:4/3;margin:0 auto 12px}}" +
      ".mr-pdpx-featured-body{flex:1 1 auto;min-width:0;padding:0}" +
      ".mr-pdpx-featured-title{font-size:15px;font-weight:600;line-height:1.35}" +
      ".mr-pdpx-rating{font-size:12px;color:rgba(44,44,42,.75);margin-top:3px;letter-spacing:.01em}" +
      ".mr-pdpx-reason{font-size:12.5px;line-height:1.4;color:rgba(44,44,42,.6);margin-top:3px}" +
      ".mr-pdpx-featured-price{font-size:15px;font-weight:600;margin-top:8px}" +
      ".mr-pdpx-select{margin-top:8px;max-width:100%;padding:8px 28px 8px 10px;font-size:13px;font-family:inherit;color:#2C2C2A;background:#fff;border:1px solid rgba(44,44,42,.2);border-radius:6px;appearance:none;-webkit-appearance:none;background-image:url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%232C2C2A' stroke-width='1.5'/%3E%3C/svg%3E\");background-repeat:no-repeat;background-position:right 10px center}" +
      ".mr-pdpx-cta{display:block;width:100%;margin-top:12px;padding:13px 16px;font-size:14px;font-weight:600;font-family:inherit;letter-spacing:.02em;color:#F1F0EC;background:#2C2C2A;border:none;border-radius:7px;cursor:pointer;transition:opacity .15s ease}" +
      ".mr-pdpx-cta:hover{opacity:.88}.mr-pdpx-cta:disabled{opacity:.55;cursor:default}" +
      ".mr-pdpx-added,.mr-pdpx-added:disabled{opacity:1;background:#2C2C2A;color:#F1F0EC}" +
      ".mr-pdpx-pair-wrap{background:#F1F0EC;border:1px solid rgba(44,44,42,.08);border-radius:10px;padding:16px}" +
      ".mr-pdpx-pair-reason{font-size:12.5px;font-weight:500;line-height:1.4;color:rgba(44,44,42,.78);margin:0 0 10px}" +
      ".mr-pdpx-pair{display:flex;gap:12px;align-items:stretch}" +
      ".mr-pdpx-pair-card{flex:1 1 0;min-width:0;display:flex;flex-direction:column}" +
      ".mr-pdpx-pair-media{width:100%;max-width:210px;aspect-ratio:4/3;border-radius:8px;overflow:hidden;background:#fff}" +
      ".mr-pdpx-pair-image{display:block;width:100%;height:100%;object-fit:cover;margin:0!important}" +
      ".mr-pdpx-pair-info{display:flex;flex-direction:column;flex:1 1 auto;min-width:0;margin-top:8px}" +
      ".mr-pdpx-pair-title{font-size:13.5px;font-weight:700;line-height:1.3}" +
      ".mr-pdpx-pair-colour{font-size:12px;line-height:1.3;color:rgba(44,44,42,.55);font-weight:400;margin-top:1px}" +
      ".mr-pdpx-pair-merch{font-size:12px;line-height:1.35;color:rgba(44,44,42,.6);margin-top:4px}" +
      ".mr-pdpx-pair-badge{display:inline-block;align-self:flex-start;font-size:11px;font-weight:600;color:#2C2C2A;margin-top:5px;letter-spacing:.01em;padding:3px 9px;border:1px solid rgba(44,44,42,.22);border-radius:999px;background:rgba(44,44,42,.04)}" +
      ".mr-pdpx-size-helper{font-size:11px;line-height:1.3;color:rgba(44,44,42,.55);margin-top:4px}" +
      ".mr-pdpx-pair-wrap+.mr-pdpx-subheading{margin-top:34px}" +
      ".mr-pdpx-pair-price{font-size:13.5px;font-weight:600;margin-top:4px}" +
      ".mr-pdpx-pair-btn{margin-top:auto;padding:11px 12px;font-size:13px}" +
      ".mr-pdpx-pair-info>.mr-pdpx-pair-price,.mr-pdpx-pair-info>.mr-pdpx-select{margin-bottom:10px}" +
      "@media (max-width:519px){.mr-pdpx-pair{display:block}.mr-pdpx-pair-card{flex-direction:row;gap:12px;align-items:flex-start}.mr-pdpx-pair-card+.mr-pdpx-pair-card{margin-top:14px}.mr-pdpx-pair-media{flex:0 0 110px;width:110px;height:110px;aspect-ratio:1/1}.mr-pdpx-pair-info{margin-top:0}.mr-pdpx-pair-btn{margin-top:10px;min-height:40px;font-size:12px;padding:10px 8px;white-space:normal;line-height:1.25}}" +
      ".mr-pdpx-subheading{font-size:12px;font-weight:600;letter-spacing:.02em;color:rgba(44,44,42,.55);margin:14px 0 4px}" +
      ".mr-pdpx-rows{border-top:1px solid rgba(44,44,42,.1)}" +
      ".mr-pdpx-row{display:flex;align-items:center;gap:12px;padding:10px 2px;border-bottom:1px solid rgba(44,44,42,.1)}" +
      ".mr-pdpx-row-thumb{width:56px;height:56px;flex:0 0 56px;object-fit:cover;border-radius:6px;background:#fff;margin:0!important}" +
      ".mr-pdpx-row-info{flex:1 1 auto;min-width:0}" +
      ".mr-pdpx-row-title{font-size:13px;font-weight:600;line-height:1.3}" +
      ".mr-pdpx-row-reason{font-size:11.5px;line-height:1.35;color:rgba(44,44,42,.6);margin-top:1px;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical}" +
      ".mr-pdpx-row-price{font-size:13px;font-weight:600;margin-top:2px}" +
      ".mr-pdpx-row-info .mr-pdpx-select{margin-top:6px;padding:6px 26px 6px 8px;font-size:12px}" +
      ".mr-pdpx-row-add{flex:0 0 auto;align-self:center;padding:10px 18px;min-width:112px;min-height:40px;font-size:12.5px;font-weight:600;font-family:inherit;color:#2C2C2A;background:transparent;border:1px solid rgba(44,44,42,.4);border-radius:7px;cursor:pointer;transition:background .15s ease,color .15s ease}" +
      ".mr-pdpx-row-add:hover{background:#2C2C2A;color:#F1F0EC}" +
      ".mr-pdpx-row-add:disabled{opacity:.55;cursor:default}" +
      ".mr-pdpx-row-add.mr-pdpx-added,.mr-pdpx-row-add.mr-pdpx-added:disabled{opacity:1;background:#2C2C2A;color:#F1F0EC;border-color:#2C2C2A}";
    var style = document.createElement("style");
    style.id = "mr-pdpx-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }

  function init() {
    injectStyles();
    preferredSize = readComforterSize();
    wire();
    schedule(); // initial pass
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
